import { type ReactNode, useContext, useMemo } from "react";
import { Controller } from "react-hook-form";
import { useLocalization } from "@fluent/react";
import { TextInput } from "@narmi/design_system";
import Account from "byzantine/src/Account";
import { modules } from "byzantine";
import { useRecipients } from "byzantine/src/dbbl/businessLogic/entities/recipients";
import { useCreateInternationalWireForm } from "byzantine/src/dbbl/businessLogic/modules/internationalWires";
import filters from "byzantine/src/filters";
import { ContentCard, catcat as cc } from "cerulean";

import CurrencyAmountInput from "../../../../../cerulean/src/ReactHookForm/CurrencyAmountInput";
import ComboAccountSelector from "../../../ComboAccountSelector";
import AccountContext from "../../../contexts/AccountContext";
import RecipientSelector from "../../../transfer/RecipientSelector";
import styles from "./InternationalWire.module.scss";
import WireReasonSelector from "./WireReasonSelector";
import LimitsDialog from "../../LimitsDialog";
import { useLimits } from "../../../contexts/LimitsContext";
import getLimitsForDisplay from "../../limitsForDisplay";

type FreeFormUsdProps = {
  reasonsRes: API.InternationalWireReasonResponse;
  onAddRecipientClick: () => void;
  renderRecipientInstitutionDetails: () => ReactNode;
};

const FreeFormUsd = ({
  reasonsRes,
  onAddRecipientClick,
  renderRecipientInstitutionDetails,
}: FreeFormUsdProps) => {
  const { l10n } = useLocalization();
  const { accounts } = useContext(AccountContext);
  const { control, setValue } = useCreateInternationalWireForm();
  const recipients = useRecipients();
  const limits = useLimits();
  const limitsForDisplay = useMemo(
    () =>
      getLimitsForDisplay(limits, [
        "international_wire",
        "organization_user_international_wire",
      ]),
    [limits],
  );
  const validAccounts = accounts.filter(
    (a: Account) => a.isInternal() && a.isValidTransferSource(),
  );
  const {
    templateSelected,
    requiresAmount,
    requiresMemo,
    requiresWireReason,
    requiresNote,
  } = modules.internationalWires.useConditionalDetails();

  const renderWireReasons = () => {
    const reasons = Object.values(reasonsRes)?.[0];
    if (reasons?.length) {
      return (
        <div className={cc({ "margin--bottom--l": requiresNote })}>
          <Controller
            name={"wireReason"}
            control={control as any}
            render={({
              field: { onChange: controlOnChange, value },
              fieldState: { error },
            }) => {
              const onChange = (e: string) => {
                controlOnChange(e);
                setValue(
                  "wireReasonDescription",
                  filters.humanize(
                    reasons.find((reason) => reason.code === e)?.description ||
                      "",
                  ),
                );
              };
              return (
                <WireReasonSelector
                  reasons={reasons}
                  onChange={onChange}
                  value={value}
                  label={l10n.getString("label-wire-reason")}
                  error={error?.message || ""}
                />
              );
            }}
          />
        </div>
      );
    }
    return null;
  };

  const amountField = (
    <div className="margin--bottom--l">
      <CurrencyAmountInput
        name="amount"
        control={control as any}
        label={l10n.getString("label-amount")}
        currency="USD"
      />
    </div>
  );

  const memoField = (
    <div className="margin--bottom--l">
      <Controller
        name={"memo"}
        control={control as any}
        render={({
          field: { onChange, onBlur, value, ref },
          fieldState: { error },
        }) => (
          <TextInput
            field="memo"
            onBlur={onBlur}
            ref={ref}
            label={l10n.getString("label-memo")}
            onChange={onChange}
            value={value ?? ""}
            error={error?.message}
          />
        )}
      />
    </div>
  );

  const noteField = (
    <Controller
      name={"note"}
      control={control as any}
      render={({
        field: { onChange, onBlur, value, ref },
        fieldState: { error },
      }) => (
        <TextInput
          field="note"
          onBlur={onBlur}
          ref={ref}
          label={l10n.getString("label-internal-note-optional")}
          onChange={onChange}
          value={value ?? ""}
          error={error?.message}
        />
      )}
    />
  );

  if (templateSelected) {
    const hasGapFields =
      requiresAmount || requiresMemo || requiresWireReason || requiresNote;

    return (
      <>
        {hasGapFields && (
          <ContentCard kind="bordered">
            <p className={styles.wireContainerSubHeader}>
              {l10n.getString("international-wire-form-subheader")}
            </p>
            {requiresAmount && amountField}
            {requiresMemo && memoField}
            {requiresWireReason && renderWireReasons()}
            {requiresNote && noteField}
            <LimitsDialog
              type={["international_wire"]}
              limitsForDisplay={limitsForDisplay}
            />
          </ContentCard>
        )}
        {!hasGapFields && (
          <LimitsDialog
            type={["international_wire"]}
            limitsForDisplay={limitsForDisplay}
          />
        )}
      </>
    );
  }

  return (
    <>
      <ContentCard kind="bordered">
        <p className={styles.wireContainerSubHeader}>
          {l10n.getString("international-wire-form-subheader")}
        </p>
        <div className="margin--bottom--l">
          <Controller
            name={"fromAccountId"}
            control={control as any}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <ComboAccountSelector
                value={value}
                isDestination={false}
                onChange={onChange}
                label={l10n.getString("label-from")}
                accounts={validAccounts}
                showAddExternalAccountLink={false}
                errorText={error?.message}
              />
            )}
          />
        </div>
        <div className="margin--bottom--l">
          <Controller
            name={"recipientId"}
            control={control as any}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <RecipientSelector
                onChange={onChange}
                value={value}
                label={l10n.getString("label-to")}
                recipients={recipients}
                onAddRecipientClick={onAddRecipientClick}
                error={error?.message}
                displayBottomAction
              />
            )}
          />
          {renderRecipientInstitutionDetails()}
        </div>
        {amountField}
        {memoField}
        {renderWireReasons()}
        <LimitsDialog
          type={["international_wire"]}
          limitsForDisplay={limitsForDisplay}
        />
      </ContentCard>
      <ContentCard kind="bordered">
        <p className={styles.wireContainerSubHeader}>
          {l10n.getString("header-transfer-additional-details")}
        </p>
        {noteField}
      </ContentCard>
    </>
  );
};

export default FreeFormUsd;
